/**
 * THE call this tab is currently in — deliberately independent of the router.
 *
 * A live call must outlive navigation. Teams' own web client keeps the call in
 * the SAME document and lets you walk around the app while it runs; it is only
 * the DESKTOP app that pops a call into its own window, and Microsoft documents
 * the web client's inability to do that as a limitation rather than a choice.
 *
 * We tried the separate window and paid for it three times over: the second
 * window booted with no access token and rotated the single-use refresh cookie
 * out from under the opener, signing you out of BOTH; pop-up blockers refused
 * to open it at all; and the call's state lived in a store the workspace window
 * could not see, so the "in a call" flag it set could never be cleared.
 *
 * So the call stays here, and this store is what makes that survivable: the
 * route may come and go, but the media surface is mounted from `ActiveCallHost`
 * ABOVE the router and reads its call id from here. Walking from the call to a
 * chat therefore shrinks the call to a corner tile instead of hanging up on
 * whoever you were talking to.
 *
 * It holds an id and nothing else on purpose. The grant, the roster and the
 * media session are all keyed by that id and already have their own homes; a
 * second copy here would be a second thing to invalidate.
 */
import { createStore } from '@/lib/tinyStore';

export interface LiveCall {
  readonly callId: string;
}

export const liveCallStore = createStore<LiveCall | null>(null);

export const selectLiveCall = (state: LiveCall | null): LiveCall | null => state;

export const liveCallActions = {
  /**
   * Idempotent: answering, placing and simply landing on `/call/:id` all funnel
   * here, and re-entering the call you are already in must not replace the
   * object — the host would remount the media surface and drop the call.
   */
  enter(callId: string): void {
    if (callId === '') return;
    liveCallStore.set((current) =>
      current !== null && current.callId === callId ? current : { callId },
    );
  },

  /** Called with an id, this only clears when that id is the live one. */
  leave(callId?: string): void {
    liveCallStore.set((current) =>
      current === null || (callId !== undefined && current.callId !== callId) ? current : null,
    );
  },
};
